import { ClientItemId, MailboxType } from 'owa-client-ids';
import BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import ConversationNodeSortOrder from 'owa-service/lib/contract/ConversationNodeSortOrder';
import ConversationRequestType from 'owa-service/lib/contract/ConversationRequestType';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import GetConversationItemsAction from 'owa-service/lib/contract/GetConversationItemsAction';
import GetConversationItemsJsonResponse from 'owa-service/lib/contract/GetConversationItemsJsonResponse';
import GetConversationItemsRequest from 'owa-service/lib/contract/GetConversationItemsRequest';
import ItemId from 'owa-service/lib/contract/ItemId';
import ItemResponseShape from 'owa-service/lib/contract/ItemResponseShape';
import conversationRequestType from 'owa-service/lib/factory/conversationRequestType';
import folderId from 'owa-service/lib/factory/folderId';
import getConversationItemsJsonRequest from 'owa-service/lib/factory/getConversationItemsJsonRequest';
import getConversationItemsRequest from 'owa-service/lib/factory/getConversationItemsRequest';
import itemId from 'owa-service/lib/factory/itemId';
import getConversationItemsOperation from 'owa-service/lib/operation/getConversationItemsOperation';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

// Ignore these folders('journal', 'outbox', 'syncissues') in GCI
// The folder list is the same with the GEEK_FOLDERS in owa-folders/lib/utils/folderUtility.ts
const FOLDERS_TO_INGORE_NAMES: DistinguishedFolderIdName[] = ['journal', 'outbox', 'syncissues'];

function convertConversationSortOrder(
    conversationSortOrder: ConversationSortOrder
): ConversationNodeSortOrder {
    // Note: only date order ascending is treated specially, all other values are treated as date order descending (the default).
    return conversationSortOrder == 'ChronologicalNewestOnBottom'
        ? 'DateOrderAscending'
        : 'DateOrderDescending';
}

function getIsGroupMailbox(conversationId: ItemId): boolean {
    const mailboxInfo = (conversationId as ClientItemId).mailboxInfo;
    return mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox;
}

function getFoldersToIgnore(isGroupMailbox: boolean): BaseFolderId[] {
    const foldersToIgnore: BaseFolderId[] = [];

    if (!isGroupMailbox) {
        FOLDERS_TO_INGORE_NAMES.forEach(folderName => {
            const ignoreFolderId = folderNameToId(folderName);
            if (ignoreFolderId) {
                foldersToIgnore.push(folderId({ Id: ignoreFolderId }));
            }
        });
    }
    return foldersToIgnore;
}

function configureRequestBody(
    conversationToLoad: ConversationRequestType,
    responseShape: ItemResponseShape,
    conversationSortOrder: ConversationSortOrder,
    maxItemsToReturn: number,
    shouldReturnModernConversationItems: boolean,
    action: GetConversationItemsAction,
    isGroupMailbox: boolean
): GetConversationItemsRequest {
    return getConversationItemsRequest({
        Conversations: [conversationToLoad],
        ItemShape: responseShape,
        ShapeName: 'ItemPart',
        SortOrder: convertConversationSortOrder(conversationSortOrder),
        MaxItemsToReturn: maxItemsToReturn,
        ReturnModernConversationItems: shouldReturnModernConversationItems,
        Action: action,
        FoldersToIgnore: getFoldersToIgnore(isGroupMailbox),
    });
}

export default function getConversationItemsService(
    conversationId: ItemId,
    responseShape: ItemResponseShape,
    syncState: string,
    maxItemsToReturn: number,
    conversationSortOrder: ConversationSortOrder,
    shouldUseEmptySyncState: boolean,
    shouldReturnModernConversationItems: boolean,
    action: GetConversationItemsAction = 'Default',
    options?: RequestOptions
): Promise<GetConversationItemsJsonResponse> {
    const conversation = conversationRequestType({ ConversationId: itemId(conversationId) });
    conversation.SyncState = !shouldUseEmptySyncState ? syncState : '';
    const isGroupMailbox = getIsGroupMailbox(conversationId);

    const requestBody = configureRequestBody(
        conversation,
        responseShape,
        conversationSortOrder,
        maxItemsToReturn,
        shouldReturnModernConversationItems,
        action,
        isGroupMailbox
    );

    const jsonRequestHeader = getJsonRequestHeader();
    jsonRequestHeader.RequestServerVersion = 'V2017_08_18';

    return getConversationItemsOperation(
        getConversationItemsJsonRequest({
            Header: jsonRequestHeader,
            Body: requestBody,
        }),
        options
    ).then(response => {
        return response.Body.ResponseMessages.Items[0] as GetConversationItemsJsonResponse;
    });
}
